import { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

function monthName(m) {
  return ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'][m - 1];
}

function keyOf(y, m) {
  return `${y}-${String(m).padStart(2, '0')}`;
}

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) throw new Error('Kunne ikke hente statistikk');
        const data = await res.json();
        setStats(data);
      } catch (err) {
        setError('Feil ved henting av statistikk');
      }
    }
    fetchStats();
  }, []);

  // ---- Tall for SUMMARIES (inkluderer gebyrer om tilgjengelig) ----
  const {
    totalUsers = 0,
    totalProducts = 0,
    totalOrders = 0,

    // Ordrer
    completedRevenue: ordersCompletedRevenueRaw = 0,
    pendingRevenue: ordersPendingRevenueRaw = 0,
    totalRevenue: ordersTotalRevenueRaw = 0,

    // Gebyrer – valgfritt hvis backend sender dette
    chargesSettled: chargesSettledRaw = 0,
    chargesUnsettled: chargesUnsettledRaw = 0,

    // Per måned arrays – også valgfritt
    ordersPerMonth = [],
    chargesPerMonth = [],
  } = stats || {};

  const ordersCompletedRevenue = Number(ordersCompletedRevenueRaw) || 0;
  const ordersPendingRevenue   = Number(ordersPendingRevenueRaw) || 0;
  const ordersTotalRevenue     = Number(ordersTotalRevenueRaw) || (ordersCompletedRevenue + ordersPendingRevenue);

  const chargesSettled   = Number(chargesSettledRaw) || 0;
  const chargesUnsettled = Number(chargesUnsettledRaw) || 0;

  // Kombinerte summer (ordrer + gebyrer)
  const combinedCompletedRevenue = ordersCompletedRevenue + chargesSettled;
  const combinedPendingRevenue   = ordersPendingRevenue + chargesUnsettled;
  const combinedTotalRevenue     = ordersTotalRevenue + chargesSettled + chargesUnsettled;

  // ---- Grafdata (kombiner ordrer + gebyrer per måned) ----
  const chartData = useMemo(() => {
    // Bygg maps for ordrer
    const ordersMap = new Map();
    for (const { year, month, total = 0, completed = 0, pending = 0 } of (ordersPerMonth || [])) {
      ordersMap.set(keyOf(year, month), { year, month, total, completed, pending });
    }

    // Bygg maps for gebyrer (settled/unsettled) hvis finnes
    const chargesMap = new Map();
    for (const { year, month, settled = 0, unsettled = 0 } of (chargesPerMonth || [])) {
      chargesMap.set(keyOf(year, month), { year, month, settled, unsettled });
    }

    // Union av alle måneder i ordrer og gebyrer
    const keys = new Set([...ordersMap.keys(), ...chargesMap.keys()]);

    // Lag liste med kombinert tall per måned
    const rows = [...keys].map(k => {
      const o = ordersMap.get(k) || {};
      const c = chargesMap.get(k) || {};

      const year  = o.year  ?? c.year  ?? new Date().getFullYear();
      const month = o.month ?? c.month ?? (new Date().getMonth() + 1);

      const ordersTotal     = Number(o.total)     || 0;
      const ordersCompleted = Number(o.completed) || 0;
      const ordersPending   = Number(o.pending)   || 0;

      const chargesSettledM   = Number(c.settled)   || 0;
      const chargesUnsettledM = Number(c.unsettled) || 0;

      // Kombinér:
      const total     = ordersTotal + chargesSettledM + chargesUnsettledM;
      const completed = ordersCompleted + chargesSettledM;
      const pending   = ordersPending + chargesUnsettledM;

      return {
        key: k,
        sortDate: new Date(year, month - 1, 1).getTime(),
        name: `${monthName(month)} ${String(year).slice(-2)}`,
        total,
        completed,
        pending,
      };
    });

    // Sorter på dato og (om ønskelig) begrens til siste 12
    rows.sort((a, b) => a.sortDate - b.sortDate);
    const last12 = rows.slice(-12);

    return last12;
  }, [ordersPerMonth, chargesPerMonth]);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">Statistikk</h2>
      {error && <p className="text-red-500">{error}</p>}
      {stats ? (
        <div>
          <ul className="space-y-2 mb-6">
            <li><strong>Kunder:</strong> {totalUsers}</li>
            <li><strong>Produkter:</strong> {totalProducts}</li>
            <li><strong>Ordrer:</strong> {totalOrders}</li>

            <li>
              <strong>Totalt salg (fullført):</strong>{' '}
              {combinedCompletedRevenue.toLocaleString('no-NO')} kr
              { (chargesSettled > 0) && (
                <span className="text-gray-500"> &nbsp; (inkl. oppgjorte gebyrer: {chargesSettled.toLocaleString('no-NO')} kr)</span>
              )}
            </li>

            <li>
              <strong>Ufakturert / ikke fullført:</strong>{' '}
              {combinedPendingRevenue.toLocaleString('no-NO')} kr
              { (chargesUnsettled > 0) && (
                <span className="text-gray-500"> &nbsp; (inkl. uoppgjorte gebyrer: {chargesUnsettled.toLocaleString('no-NO')} kr)</span>
              )}
            </li>

            <li>
              <strong>Sum alle ordre:</strong>{' '}
              {combinedTotalRevenue.toLocaleString('no-NO')} kr
            </li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">Omsetning siste 12 måneder</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip
                formatter={v => (v ?? 0).toLocaleString('no-NO') + ' kr'}
                labelFormatter={label => `Måned: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                name="Totalt (ordre + gebyrer)"
                stroke="#000099"
                strokeWidth={3}
                dot
              />
              <Line
                type="monotone"
                dataKey="completed"
                name="Fakturert (fullført + oppgjorte gebyrer)"
                stroke="#008F4C"
                strokeWidth={3}
                dot
              />
              <Line
                type="monotone"
                dataKey="pending"
                name="Ufakturert (ordre + uoppgjorte gebyrer)"
                stroke="#E04000"
                strokeWidth={3}
                dot
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p>Laster statistikk...</p>
      )}
    </div>
  );
}
